import { CalendarClock, ImageIcon, MessageSquareText, Scissors } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface ConnectCapabilitiesCardProps {
  /**
   * Opens the connect dialog (which then offers OAuth vs managed flow).
   * Single-button entry point — the boss asked to collapse the two
   * previous side-by-side cards into one CTA backed by a dialog.
   */
  onConnect: () => void;
  isLoading: boolean;
}

interface Capability {
  icon: typeof MessageSquareText;
  titleKey: string;
  descKey: string;
  /**
   * True for capabilities we list on the marketing card but haven't
   * actually shipped yet. Shows an "Upcoming" badge so we don't mislead
   * salons into expecting hours/services/posts sync to work after they
   * connect. Today only reviews sync is live; the rest are planned.
   */
  upcoming?: boolean;
}

/**
 * The "before you connect" pitch shown on the Google Business settings
 * page. Lists the four capabilities Google Business integration unlocks
 * (reviews + storefront sync, opening hours, services, profile posts /
 * photos) with a single primary CTA. Replaces the previous two cards
 * (Direct connect + Managed flow) per design.
 */
export function ConnectCapabilitiesCard({ onConnect, isLoading }: ConnectCapabilitiesCardProps) {
  const { t } = useTranslation("settings");

  const capabilities: Capability[] = [
    {
      icon: MessageSquareText,
      titleKey: "reviews.connect.capabilities.reviews.title",
      descKey: "reviews.connect.capabilities.reviews.description",
    },
    {
      icon: CalendarClock,
      titleKey: "reviews.connect.capabilities.hours.title",
      descKey: "reviews.connect.capabilities.hours.description",
      upcoming: true,
    },
    {
      icon: Scissors,
      titleKey: "reviews.connect.capabilities.services.title",
      descKey: "reviews.connect.capabilities.services.description",
      upcoming: true,
    },
    {
      icon: ImageIcon,
      titleKey: "reviews.connect.capabilities.profile.title",
      descKey: "reviews.connect.capabilities.profile.description",
      upcoming: true,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("reviews.connect.capabilitiesTitle")}</CardTitle>
        <CardDescription>{t("reviews.connect.capabilitiesDescription")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <ul className="grid gap-4 sm:grid-cols-2">
          {capabilities.map(({ icon: Icon, titleKey, descKey, upcoming }) => (
            <li key={titleKey} className="flex items-start gap-3">
              <div className="rounded-md bg-indigo-50 dark:bg-indigo-950 p-2 shrink-0">
                <Icon className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-sm">{t(titleKey)}</p>
                  {upcoming && (
                    <Badge
                      variant="outline"
                      className="text-[10px] uppercase tracking-wide px-1.5 py-0 h-4 font-medium"
                    >
                      {t("reviews.connect.capabilities.upcoming")}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{t(descKey)}</p>
              </div>
            </li>
          ))}
        </ul>

        <div>
          <Button onClick={onConnect} disabled={isLoading} size="lg">
            {isLoading ? t("reviews.connect.actionLoading") : t("reviews.connect.action")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
